import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckSquare,
  FileSpreadsheet,
  FileText,
  Layers,
  Loader2,
  Radar,
  Sparkles,
  Square,
  X,
} from "lucide-react";
import {
  approveItem,
  bulkTransition,
  explainItem,
  loadPlan,
  loadPlanImpact,
  modifyItem,
  planCsvUrl,
  planPdfUrl,
  rejectItem,
  type BulkAction,
  type CleanupCandidate,
  type CleanupPlan,
  type Explanation,
  type PlanImpactSummary,
  type PlanItem,
} from "@/lib/api";
import { ActionBadge } from "@/components/ActionBadge";
import { HealthScoreBar } from "@/components/HealthScoreBar";
import { RiskBadge } from "@/components/RiskBadge";
import { StateBadge } from "@/components/StateBadge";
import { formatDate } from "@/lib/format";

export function PlanDetailPage() {
  const { planId = "" } = useParams<{ planId: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["curate-plan", planId],
    queryFn: () => loadPlan(planId),
    enabled: planId.length > 0,
  });
  const impact = useQuery({
    queryKey: ["curate-plan", planId, "impact"],
    queryFn: () => loadPlanImpact(planId),
    enabled: planId.length > 0,
  });

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <Link
        to="/plans"
        className="inline-flex items-center gap-1 text-2xs font-medium text-curate-slate hover:text-curate-ink"
      >
        <ArrowLeft size={12} /> All plans
      </Link>
      {isLoading && <div className="mt-6 h-32 animate-pulse rounded-xl2 bg-curate-mist/60" />}
      {error && (
        <div className="mt-6 rounded-xl2 border border-curate-danger/30 bg-curate-danger/5 p-4 text-sm text-curate-danger">
          Couldn't load plan — {(error as Error).message}
        </div>
      )}
      {data && <PlanBody plan={data} impact={impact.data} />}
    </div>
  );
}

function PlanBody({
  plan,
  impact,
}: {
  plan: CleanupPlan;
  impact: PlanImpactSummary | undefined;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const proposed = useMemo(
    () => plan.items.filter((i) => i.state === "PROPOSED"),
    [plan.items],
  );
  const allProposedSelected =
    proposed.length > 0 && proposed.every((i) => selected.has(i.id));

  const selectAllProposed = () => {
    if (allProposedSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(proposed.map((i) => i.id)));
    }
  };

  const bulk = useMutation({
    mutationFn: (action: BulkAction) =>
      bulkTransition(plan.id, action, Array.from(selected)),
    onSuccess: (resp, action) => {
      setSelected(new Set());
      setFeedback(
        `${action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Modified"}: ${resp.applied} of ${resp.requested}` +
          (resp.skipped > 0 ? ` (${resp.skipped} skipped — already terminal)` : ""),
      );
      queryClient.invalidateQueries({ queryKey: ["curate-plan", plan.id] });
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } } };
      setFeedback(`Bulk failed: ${err.response?.data?.message ?? String(e)}`);
    },
  });

  return (
    <>
      <header className="mt-3 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-curate-ink">
            {plan.scope.type === "all" ? "All assets" : plan.scope.value}
          </h1>
          <p className="mt-0.5 text-sm text-curate-slate">
            Generated {formatDate(plan.createdAt)} · {plan.summary.totalItems} item
            {plan.summary.totalItems === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <a
            href={planCsvUrl(plan.id)}
            className="inline-flex items-center gap-1.5 rounded-md border border-curate-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-curate-ink hover:bg-curate-mist"
            title="Download CSV (working data — one row per item)"
          >
            <FileSpreadsheet size={12} aria-hidden /> Export CSV
          </a>
          <a
            href={planPdfUrl(plan.id)}
            className="inline-flex items-center gap-1.5 rounded-md border border-curate-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-curate-ink hover:bg-curate-mist"
            title="Download executive PDF for stakeholder sign-off"
          >
            <FileText size={12} aria-hidden /> Export PDF
          </a>
        </div>
      </header>

      {impact && <ImpactCard impact={impact} />}

      {proposed.length > 0 && (
        <BulkBar
          selectedCount={selected.size}
          allProposedSelected={allProposedSelected}
          proposedCount={proposed.length}
          onToggleAll={selectAllProposed}
          onBulk={(a) => bulk.mutate(a)}
          isPending={bulk.isPending}
          feedback={feedback}
        />
      )}

      <ul data-tour-id="plan-items" className="mt-3 space-y-3">
        {plan.items.map((item) => (
          <li key={item.id}>
            <PlanItemRow
              planId={plan.id}
              item={item}
              selected={selected.has(item.id)}
              onToggle={() => toggle(item.id)}
            />
          </li>
        ))}
      </ul>
    </>
  );
}

function ImpactCard({ impact }: { impact: PlanImpactSummary }) {
  const archive = impact.byAction.ARCHIVE ?? 0;
  const consolidate = impact.byAction.CONSOLIDATE ?? 0;
  const reassign = impact.byAction.REASSIGN_OWNER ?? 0;
  const completeAt = impact.earliestCoolingOffComplete
    ? formatDate(impact.earliestCoolingOffComplete)
    : null;

  return (
    <section
      className="mt-6 rounded-xl2 border border-curate-slate/20 bg-white p-4 shadow-card"
      aria-label="Plan impact summary"
    >
      <header className="mb-3 flex items-center gap-2 text-sm font-semibold text-curate-ink">
        <Radar size={14} aria-hidden /> Impact preview
      </header>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Pending"
          value={impact.proposedItems.toString()}
          tone={impact.proposedItems > 0 ? "info" : "muted"}
          hint="Items still awaiting a decision"
        />
        <Stat
          label="Approved"
          value={impact.approvedItems.toString()}
          tone={impact.approvedItems > 0 ? "ok" : "muted"}
          hint="Will execute after cooling-off"
        />
        <Stat
          label="Rejected"
          value={impact.rejectedItems.toString()}
          tone="muted"
        />
        <Stat
          label="Modified"
          value={impact.modifiedItems.toString()}
          tone="muted"
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-curate-slate/15 pt-3 text-xs text-curate-ink">
        {archive > 0 && (
          <Badge>
            <span className="font-mono font-medium">{archive}</span> archive
          </Badge>
        )}
        {consolidate > 0 && (
          <Badge>
            <Layers size={11} aria-hidden />
            <span className="font-mono font-medium">{consolidate}</span> consolidate
          </Badge>
        )}
        {reassign > 0 && (
          <Badge>
            <span className="font-mono font-medium">{reassign}</span> reassign
          </Badge>
        )}
        {impact.duplicateClusterCount > 0 && (
          <Badge>
            {impact.duplicateClusterCount} duplicate cluster
            {impact.duplicateClusterCount > 1 ? "s" : ""}
          </Badge>
        )}
        {impact.highRiskApprovedCount > 0 && (
          <Badge tone="danger">
            <AlertTriangle size={11} aria-hidden />
            {impact.highRiskApprovedCount} HIGH-risk approved
          </Badge>
        )}
        {impact.estimatedBlastRadius > 0 && (
          <Badge>
            <span className="font-mono font-medium">
              {impact.estimatedBlastRadius.toLocaleString()}
            </span>{" "}
            downstream consumers affected
          </Badge>
        )}
        {completeAt && (
          <Badge tone="info">All approved items clear cooling-off by {completeAt}</Badge>
        )}
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone: "ok" | "info" | "muted";
  hint?: string;
}) {
  const valueClass =
    tone === "ok"
      ? "text-curate-success"
      : tone === "info"
      ? "text-curate-primary"
      : "text-curate-ink/70";
  return (
    <div className="rounded-md border border-curate-slate/15 bg-curate-mist/30 p-2.5" title={hint}>
      <div className="text-2xs uppercase tracking-wide text-curate-slate">{label}</div>
      <div className={`mt-0.5 font-mono text-2xl font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: "danger" | "info";
}) {
  const cls =
    tone === "danger"
      ? "border-curate-danger/30 bg-curate-danger/10 text-curate-danger"
      : tone === "info"
      ? "border-curate-primary/30 bg-curate-primary/10 text-curate-primary"
      : "border-curate-slate/30 bg-curate-mist text-curate-ink";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs ${cls}`}>
      {children}
    </span>
  );
}

function BulkBar({
  selectedCount,
  allProposedSelected,
  proposedCount,
  onToggleAll,
  onBulk,
  isPending,
  feedback,
}: {
  selectedCount: number;
  allProposedSelected: boolean;
  proposedCount: number;
  onToggleAll: () => void;
  onBulk: (action: BulkAction) => void;
  isPending: boolean;
  feedback: string | null;
}) {
  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-curate-primary/30 bg-curate-primary/5 px-3 py-2 text-xs text-curate-ink"
      role="region"
      aria-label="Bulk actions"
    >
      <button
        onClick={onToggleAll}
        className="inline-flex items-center gap-1.5 rounded-md border border-curate-slate/30 bg-white px-2 py-1 font-medium hover:bg-curate-mist"
      >
        {allProposedSelected ? <CheckSquare size={12} /> : <Square size={12} />}
        Select all PROPOSED ({proposedCount})
      </button>
      <span className="text-curate-slate">
        {selectedCount} selected
      </span>
      <div data-tour-id="plan-apply" className="ml-auto flex items-center gap-1.5">
        <button
          onClick={() => onBulk("approve")}
          disabled={selectedCount === 0 || isPending}
          className="inline-flex items-center gap-1 rounded-md bg-curate-primary px-2.5 py-1 font-medium text-white shadow-sm hover:bg-curate-primaryDark disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          Approve selected
        </button>
        <button
          onClick={() => onBulk("reject")}
          disabled={selectedCount === 0 || isPending}
          className="inline-flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-2.5 py-1 font-medium text-curate-ink hover:bg-curate-mist disabled:cursor-not-allowed disabled:opacity-40"
        >
          <X size={12} /> Reject selected
        </button>
        <button
          onClick={() => onBulk("modify")}
          disabled={selectedCount === 0 || isPending}
          className="inline-flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-2.5 py-1 font-medium text-curate-ink hover:bg-curate-mist disabled:cursor-not-allowed disabled:opacity-40"
        >
          Modify selected
        </button>
      </div>
      {feedback && (
        <p className="basis-full text-2xs text-curate-slate" role="status">
          {feedback}
        </p>
      )}
    </div>
  );
}

function PlanItemRow({
  planId,
  item,
  selected,
  onToggle,
}: {
  planId: string;
  item: PlanItem;
  selected: boolean;
  onToggle: () => void;
}) {
  const queryClient = useQueryClient();
  const cand: CleanupCandidate = item.candidate;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["curate-plan", planId] });

  const approve = useMutation({
    mutationFn: () => approveItem(planId, item.id),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: () => rejectItem(planId, item.id),
    onSuccess: invalidate,
  });
  const modify = useMutation({
    mutationFn: () => modifyItem(planId, item.id),
    onSuccess: invalidate,
  });
  const explain = useMutation<Explanation, Error>({
    mutationFn: () => explainItem(item),
  });

  const [expanded, setExpanded] = useState(false);

  const transitioning = approve.isPending || reject.isPending || modify.isPending;
  const transitionError =
    (approve.error as Error) ?? (reject.error as Error) ?? (modify.error as Error) ?? null;

  const selectable = item.state === "PROPOSED";

  return (
    <article
      className={`rounded-xl2 border bg-white p-4 shadow-card ${
        selected ? "border-curate-primary ring-2 ring-curate-primary/30" : "border-curate-slate/20"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Selection checkbox — only meaningful for PROPOSED items.
            Disabled state mirrors the "Approve" button's gating. */}
        <button
          type="button"
          onClick={selectable ? onToggle : undefined}
          disabled={!selectable}
          aria-pressed={selected}
          aria-label={selected ? "Deselect item" : "Select item"}
          className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border transition-colors ${
            selectable
              ? selected
                ? "border-curate-primary bg-curate-primary text-white"
                : "border-curate-slate/40 bg-white hover:border-curate-primary"
              : "cursor-not-allowed border-curate-slate/20 bg-curate-mist/40"
          }`}
        >
          {selected && <Check size={12} aria-hidden />}
        </button>
        <div className="flex flex-1 items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-curate-slate" aria-hidden />
              <h2 className="font-medium text-curate-ink">{cand.asset.displayName}</h2>
              <span className="font-mono text-2xs text-curate-slate">
                {cand.asset.kind.toLowerCase()}
              </span>
            </div>
            <p className="mt-0.5 text-2xs text-curate-slate">{cand.asset.folderPath}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <ActionBadge action={cand.action} />
              <RiskBadge level={cand.riskLevel} />
              <StateBadge state={item.state} />
              {cand.coolingOffDays > 0 && (
                <span className="text-2xs text-curate-slate">
                  {cand.coolingOffDays}-day cooling-off
                </span>
              )}
              {cand.blastRadius != null && cand.blastRadius > 0 && (
                <span className="text-2xs text-curate-slate">
                  {cand.blastRadius} downstream
                </span>
              )}
            </div>
            {cand.reasons.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs text-curate-ink/80">
                {cand.reasons.slice(0, expanded ? cand.reasons.length : 2).map((r, i) => (
                  <li key={i} className="leading-tight">— {r}</li>
                ))}
                {cand.reasons.length > 2 && !expanded && (
                  <li>
                    <button
                      onClick={() => setExpanded(true)}
                      className="text-2xs font-medium text-curate-primary hover:underline"
                    >
                      show {cand.reasons.length - 2} more
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
          <HealthScoreBar value={cand.score.value} confidence={cand.score.confidence} />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={() => approve.mutate()}
          disabled={transitioning || item.state !== "PROPOSED"}
          className="inline-flex items-center gap-1 rounded-md bg-curate-primary px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-curate-primaryDark disabled:opacity-40"
        >
          {approve.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          Approve
        </button>
        <button
          onClick={() => reject.mutate()}
          disabled={transitioning || item.state === "REJECTED" || item.state === "REVERTED"}
          className="inline-flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-3 py-1 text-xs font-medium text-curate-ink hover:bg-curate-mist disabled:opacity-40"
        >
          <X size={12} /> Reject
        </button>
        <button
          onClick={() => modify.mutate()}
          disabled={transitioning || item.state !== "PROPOSED"}
          className="inline-flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-3 py-1 text-xs font-medium text-curate-ink hover:bg-curate-mist disabled:opacity-40"
        >
          Modify
        </button>
        <button
          onClick={() => explain.mutate()}
          disabled={explain.isPending}
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-curate-accent/40 bg-curate-accent/10 px-3 py-1 text-xs font-medium text-curate-ink hover:bg-curate-accent/20 disabled:opacity-40"
        >
          {explain.isPending
            ? <Loader2 size={12} className="animate-spin" />
            : <Sparkles size={12} />}
          Explain
        </button>
      </div>

      {transitionError && (
        <p className="mt-2 text-2xs text-curate-danger">
          {transitionError.message}
        </p>
      )}
      {explain.data && <ExplanationBlock data={explain.data} />}
      {explain.error && (
        <p className="mt-2 text-2xs text-curate-danger">
          Explain failed — {(explain.error as Error).message}
        </p>
      )}
    </article>
  );
}

function ExplanationBlock({ data }: { data: Explanation }) {
  const fallback = !data.provider || data.provider === "fallback";
  return (
    <aside className="mt-3 rounded-md border border-curate-accent/30 bg-curate-accent/5 p-3 text-sm text-curate-ink">
      {data.text}
      <div className="mt-1 text-2xs text-curate-slate">
        {fallback
          ? "Deterministic fallback rationale — no LLM gateway configured for this deployment."
          : `Generated by ${data.provider}${data.promptVersion ? ` · prompt v${data.promptVersion}` : ""}`}
      </div>
    </aside>
  );
}
